import { GiftProductType } from './gift';

export interface GiftProductTarget {
  product_type: GiftProductType;
  membership_id: number | null;
  entry_pass_id: number | null;
  voucher_pack_id: number | null;
}

export interface GiftableProduct {
  product_type: GiftProductType;
  product_id: number | null;
  name: string;
  price_display: string;
  gift_url: string;
  tag_text: string;
  tag_color_hex: string;
  short_description: string;
}

export interface GiftCenterCarouselImage {
  id: number;
  url: string;
  order_number: number;
  image: string | null;
  button_text: string;
  product_type: GiftProductType | '';
  membership_id: number | null;
  entry_pass_id: number | null;
  voucher_pack_id: number | null;
  product_name: string | null;
  is_giftable: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface GiftCenterFeaturedGift extends GiftProductTarget {
  id: number | null;
  order_number: number;
  product_name: string;
  price_display: string;
  is_giftable: boolean;
  tag_text: string;
  tag_color_hex: string;
  short_description: string;
}

export interface GiftProductPresentation extends GiftProductTarget {
  id: number;
  product_name: string;
  tag_text: string;
  tag_color_hex: string;
  short_description: string;
}

export interface GiftProductPresentationWrite extends GiftProductTarget {
  tag_text: string;
  tag_color_hex: string;
  short_description: string;
}

export interface GiftCenterHomePreview {
  preview_url: string;
  give_preview_url: string;
  expires_at: string;
}

/**
 * The unsaved home page settings to preview. Featured gifts are in display
 * order; presentations hold every giftable product, a blank one meaning no tag.
 */
export interface GiftCenterHomePreviewPayload {
  gift_center_alert_enabled: boolean;
  gift_center_alert_text: string;
  gift_center_alert_color_hex: string;
  gift_center_alert_start_date: string | null;
  gift_center_alert_end_date: string | null;
  gift_center_carousel_title: string;
  featured_gifts: GiftProductTarget[];
  presentations: GiftProductPresentationWrite[];
}

/**
 * Picker groups in display order. A type with no giftable products renders no
 * group.
 */
export const GIFTABLE_PRODUCT_GROUPS: { productType: GiftProductType, label: string }[] = [
  {productType: GiftProductType.membership, label: 'Memberships'},
  {productType: GiftProductType.entryPass, label: 'Passes'},
  {productType: GiftProductType.voucherPack, label: 'Voucher Packs'},
  {productType: GiftProductType.giftCard, label: 'Gift Cards'},
];

/**
 * A string identifying one giftable product, e.g. 'MEM:12'; the gift card,
 * which has no id, is 'GCD:'.
 */
export function getGiftProductKey(productType: GiftProductType, productId: number | null): string {
  return productType + ':' + (productId === null ? '' : productId.toString());
}

export function getGiftableProductKey(product: GiftableProduct): string {
  return getGiftProductKey(product.product_type, product.product_id);
}

/** Null when the target is unset (a carousel image with no button). */
export function getGiftProductTargetKey(target: {
  product_type: GiftProductType | '';
  membership_id: number | null;
  entry_pass_id: number | null;
  voucher_pack_id: number | null;
}): string | null {
  switch (target.product_type) {
    case GiftProductType.membership:
      return getGiftProductKey(target.product_type, target.membership_id);
    case GiftProductType.entryPass:
      return getGiftProductKey(target.product_type, target.entry_pass_id);
    case GiftProductType.voucherPack:
      return getGiftProductKey(target.product_type, target.voucher_pack_id);
    case GiftProductType.giftCard:
      return getGiftProductKey(target.product_type, null);
    default:
      return null;
  }
}

export function getGiftProductTargetForGiftableProduct(product: GiftableProduct): GiftProductTarget {
  return {
    product_type: product.product_type,
    membership_id: product.product_type === GiftProductType.membership ? product.product_id : null,
    entry_pass_id: product.product_type === GiftProductType.entryPass ? product.product_id : null,
    voucher_pack_id: product.product_type === GiftProductType.voucherPack ? product.product_id : null,
  };
}
